import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, FormEvent } from 'react';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { api, ApiError } from '../lib/api';
import { useAssetUrl } from '../lib/useAssetUrl';
import type { AgentId, KnowledgeAsset } from '@shared/types';
import { MAX_UPLOAD_LABEL, uploadSizeError } from '@shared/uploads';

/**
 * Knowledge — the Company Brain feeder. A note composer and a file drop write
 * into the shared brain every agent reads (route to `shared` for the whole team,
 * or to one teammate to top up just their memory). Notes commit to git via a
 * runner task; binaries land in R2. Nothing lives only in the UI.
 */

const AGENTS: Array<{ id: AgentId; name: string }> = [
  { id: 'emma', name: 'Emma' },
  { id: 'milo', name: 'Milo' },
  { id: 'leo', name: 'Leo' },
  { id: 'quinn', name: 'Quinn' },
  { id: 'iris', name: 'Iris' },
  { id: 'nora', name: 'Nora' },
  { id: 'grace', name: 'Grace' },
];

const KINDS: Array<KnowledgeAsset['kind']> = ['image', 'video', 'logo', 'doc', 'note'];

const field: CSSProperties = {
  width: '100%',
  background: 'var(--panel-2)',
  color: 'var(--text)',
  border: '1px solid var(--line-2)',
  borderRadius: 'var(--r-ctl)',
  padding: '11px 13px',
  fontFamily: 'var(--font)',
  fontSize: 14,
  lineHeight: 1.5,
  outline: 'none',
};
const selectStyle: CSSProperties = { ...field, appearance: 'none', cursor: 'pointer' };
const textareaStyle: CSSProperties = { ...field, minHeight: 128, resize: 'vertical' };
const labelStyle: CSSProperties = { display: 'block', marginBottom: 7 };
const groupStyle: CSSProperties = { marginBottom: 14 };
const glyphBox: CSSProperties = {
  width: 52,
  height: 52,
  flexShrink: 0,
  borderRadius: 10,
  border: '1px solid var(--line)',
  background: 'rgba(var(--ice-rgb),.06)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 20,
  color: 'var(--dim)',
  overflow: 'hidden',
};

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: unknown } | null;
    if (b && typeof b.error === 'string') return b.error;
    return `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}

function parseTags(s: string): string[] | undefined {
  const t = s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return t.length ? t : undefined;
}

function inferKind(file: File): KnowledgeAsset['kind'] {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'doc';
}

function stripExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = r.result;
      if (typeof res !== 'string') return reject(new Error('Could not read file'));
      const comma = res.indexOf(',');
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    r.onerror = () => reject(r.error ?? new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

function glyphFor(kind: KnowledgeAsset['kind']): string {
  switch (kind) {
    case 'image':
      return '▦';
    case 'logo':
      return '◆';
    case 'video':
      return '▶';
    case 'doc':
      return '▤';
    default:
      return '✎';
  }
}

function routedLabel(v: string): string {
  if (v === 'shared') return 'Shared';
  return AGENTS.find((a) => a.id === v)?.name ?? v;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** R2 thumbnail for image/logo assets; falls back to a kind glyph if none loads. */
function Thumb({ asset }: { asset: KnowledgeAsset }) {
  const [broken, setBroken] = useState(false);
  const wantsImg = (asset.kind === 'image' || asset.kind === 'logo') && !!asset.r2_key && !broken;
  const url = useAssetUrl(wantsImg ? asset.r2_key : null); // tokened /assets URL (async)
  if (wantsImg && url) {
    return (
      <img
        src={url}
        alt={asset.title}
        onError={() => setBroken(true)}
        style={{ ...glyphBox, objectFit: 'cover' }}
      />
    );
  }
  return <div style={glyphBox}>{glyphFor(asset.kind)}</div>;
}

export default function Knowledge() {
  const [assets, setAssets] = useState<KnowledgeAsset[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  // note composer
  const [nTitle, setNTitle] = useState('');
  const [nBody, setNBody] = useState('');
  const [nRoute, setNRoute] = useState('shared');
  const [nTags, setNTags] = useState('');
  const [nBusy, setNBusy] = useState(false);
  const [nErr, setNErr] = useState<string | null>(null);

  // file upload
  const [file, setFile] = useState<File | null>(null);
  const [uTitle, setUTitle] = useState('');
  const [uKind, setUKind] = useState<KnowledgeAsset['kind']>('doc');
  const [uRoute, setURoute] = useState('shared');
  const [uTags, setUTags] = useState('');
  const [uBusy, setUBusy] = useState(false);
  const [uErr, setUErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    api
      .get<KnowledgeAsset[]>('/knowledge')
      .then((rows) => {
        setAssets(rows);
        setLoadError(null);
      })
      .catch((e) => setLoadError(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  function flash(msg: string) {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2800);
  }

  function pickFile(f: File) {
    // Guard the size BEFORE we base64-encode + POST: a too-big file would otherwise
    // freeze the phone while encoding and then fail the upload with a cryptic error.
    const sizeErr = uploadSizeError(f.size);
    if (sizeErr) {
      setFile(null);
      setUTitle('');
      setUErr(sizeErr);
      if (fileInput.current) fileInput.current.value = '';
      return;
    }
    setFile(f);
    setUTitle(stripExt(f.name));
    setUKind(inferKind(f));
    setUErr(null);
  }

  function resetUpload() {
    setFile(null);
    setUTitle('');
    setUKind('doc');
    setUTags('');
    setURoute('shared');
    if (fileInput.current) fileInput.current.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }

  async function submitNote(e: FormEvent) {
    e.preventDefault();
    if (!nTitle.trim() || !nBody.trim()) return;
    setNBusy(true);
    setNErr(null);
    try {
      const asset = await api.post<KnowledgeAsset>('/knowledge/note', {
        title: nTitle.trim(),
        body_md: nBody,
        routed_to: [nRoute],
        tags: parseTags(nTags),
      });
      setAssets((cur) => [asset, ...(cur ?? [])]);
      setNTitle('');
      setNBody('');
      setNTags('');
      setNRoute('shared');
      flash(`Filed “${asset.title}” to the brain`);
    } catch (err) {
      setNErr(errMsg(err));
    } finally {
      setNBusy(false);
    }
  }

  async function submitUpload(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setUBusy(true);
    setUErr(null);
    try {
      const contentBase64 = await fileToBase64(file);
      const asset = await api.post<KnowledgeAsset>('/knowledge/upload', {
        kind: uKind,
        title: uTitle.trim() || file.name,
        filename: file.name,
        contentBase64,
        routed_to: [uRoute],
        tags: parseTags(uTags),
      });
      setAssets((cur) => [asset, ...(cur ?? [])]);
      resetUpload();
      flash(`Uploaded “${asset.title}” to the brain`);
    } catch (err) {
      setUErr(errMsg(err));
    } finally {
      setUBusy(false);
    }
  }

  return (
    <Layout
      title="Knowledge"
      actions={
        <button className="btn btn-ghost sm" onClick={load}>
          Refresh
        </button>
      }
    >
      {/* what this is */}
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <Orb agent="emma" size="xs" />
        <div>
          <div className="t-label">The Company Brain</div>
          <p className="small" style={{ marginTop: 6 }}>
            Everything you file here lands in the shared brain every agent reads before they work. Route to{' '}
            <b style={{ color: 'var(--text)' }}>Shared</b> for the whole team, or to one teammate to top up just
            their memory. Notes are committed into <span className="mono">knowledge/</span>; files are stored in R2 —
            nothing lives only here.
          </p>
        </div>
      </div>

      {/* composers */}
      <div className="grid g2" style={{ marginBottom: 22 }}>
        {/* note composer */}
        <form className="card" onSubmit={submitNote}>
          <div className="t-label" style={{ marginBottom: 14 }}>
            Write a note
          </div>
          <div style={groupStyle}>
            <label className="t-label" style={labelStyle}>
              Title
            </label>
            <input
              style={field}
              value={nTitle}
              onChange={(e) => setNTitle(e.target.value)}
              placeholder="e.g. Brand voice — how we talk about the pad"
            />
          </div>
          <div style={groupStyle}>
            <label className="t-label" style={labelStyle}>
              Note
            </label>
            <textarea
              style={textareaStyle}
              value={nBody}
              onChange={(e) => setNBody(e.target.value)}
              placeholder="Markdown welcome. Jot the fact, the reason, the do/don't…"
            />
          </div>
          <div className="grid g2" style={{ gap: 12, marginBottom: 14 }}>
            <div>
              <label className="t-label" style={labelStyle}>
                Route to
              </label>
              <select style={selectStyle} value={nRoute} onChange={(e) => setNRoute(e.target.value)}>
                <option value="shared">Shared — every agent</option>
                {AGENTS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} only
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="t-label" style={labelStyle}>
                Tags <span className="faint">(optional, comma-sep)</span>
              </label>
              <input
                style={field}
                value={nTags}
                onChange={(e) => setNTags(e.target.value)}
                placeholder="brand, voice"
              />
            </div>
          </div>
          {nErr && (
            <p className="small" style={{ color: 'var(--coral)', marginBottom: 12 }}>
              {nErr}
            </p>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={nBusy || !nTitle.trim() || !nBody.trim()}
          >
            {nBusy ? 'Filing…' : 'File note to brain'}
          </button>
        </form>

        {/* file upload */}
        <form className="card" onSubmit={submitUpload}>
          <div className="t-label" style={{ marginBottom: 14 }}>
            Drop a file
          </div>

          <div
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            style={{
              border: `1px dashed ${dragOver ? 'var(--ice)' : 'var(--line-2)'}`,
              borderRadius: 'var(--r)',
              padding: '24px 18px',
              textAlign: 'center',
              background: dragOver ? 'rgba(var(--ice-rgb),.06)' : 'transparent',
              transition: 'all .16s',
              cursor: 'pointer',
            }}
          >
            {file ? (
              <div>
                <div className="mono" style={{ fontSize: 13, color: 'var(--text)' }}>
                  {file.name}
                </div>
                <div className="small faint" style={{ marginTop: 4 }}>
                  {(file.size / 1024).toFixed(0)} KB · click to swap
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 22, color: 'var(--dim)' }} aria-hidden>
                  ❋
                </div>
                <div className="small" style={{ marginTop: 6 }}>
                  Drag a logo, product photo, video or doc here
                </div>
                <div className="faint small" style={{ marginTop: 2 }}>
                  or click to browse · files up to {MAX_UPLOAD_LABEL}
                </div>
              </div>
            )}
            <input
              ref={fileInput}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
              }}
            />
          </div>

          {file && (
            <div style={{ marginTop: 14 }}>
              <div style={groupStyle}>
                <label className="t-label" style={labelStyle}>
                  Title
                </label>
                <input style={field} value={uTitle} onChange={(e) => setUTitle(e.target.value)} />
              </div>
              <div className="grid g3" style={{ gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="t-label" style={labelStyle}>
                    Kind
                  </label>
                  <select
                    style={selectStyle}
                    value={uKind}
                    onChange={(e) => setUKind(e.target.value as KnowledgeAsset['kind'])}
                  >
                    {KINDS.filter((k) => k !== 'note').map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="t-label" style={labelStyle}>
                    Route to
                  </label>
                  <select style={selectStyle} value={uRoute} onChange={(e) => setURoute(e.target.value)}>
                    <option value="shared">Shared</option>
                    {AGENTS.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="t-label" style={labelStyle}>
                    Tags
                  </label>
                  <input
                    style={field}
                    value={uTags}
                    onChange={(e) => setUTags(e.target.value)}
                    placeholder="logo, hero"
                  />
                </div>
              </div>
            </div>
          )}

          {uErr && (
            <p className="small" style={{ color: 'var(--coral)', marginBottom: 12 }}>
              {uErr}
            </p>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="submit" className="btn btn-line" disabled={uBusy || !file}>
              {uBusy ? 'Uploading…' : 'Upload to brain'}
            </button>
            {file && !uBusy && (
              <button type="button" className="btn btn-ghost sm" onClick={resetUpload}>
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      {/* the brain contents */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <div className="t-label">In the brain</div>
        {assets && (
          <span className="mono faint" style={{ fontSize: 12 }}>
            {assets.length}
          </span>
        )}
      </div>

      {loadError && (
        <div className="card">
          <p className="small" style={{ color: 'var(--coral)' }}>
            Couldn’t load the brain — {loadError}
          </p>
          <button className="btn btn-line sm" style={{ marginTop: 12 }} onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loadError && assets === null && <p className="small dim">Loading the brain…</p>}

      {!loadError && assets && assets.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ display: 'inline-flex' }}>
            <Orb agent="emma" size="md" />
          </div>
          <p className="small" style={{ marginTop: 14 }}>
            The brain’s empty. Drop a logo, a product photo, or jot the first note — every agent will read it.
          </p>
        </div>
      )}

      {!loadError && assets && assets.length > 0 && (
        <div className="grid g3">
          {assets.map((a) => (
            <div className="card" key={a.id}>
              <div style={{ display: 'flex', gap: 12 }}>
                <Thumb asset={a} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div className="who" style={{ wordBreak: 'break-word' }}>
                      {a.title}
                    </div>
                    <span className="chip chip-dim" style={{ flexShrink: 0 }}>
                      <i />
                      {a.kind}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {(a.routed_to ?? ['shared']).map((r) => (
                      <span key={r} className={`chip ${r === 'shared' ? 'chip-ice' : 'chip-violet'}`}>
                        <i />
                        {routedLabel(r)}
                      </span>
                    ))}
                    {(a.tags ?? []).map((t) => (
                      <span key={t} className="chip chip-dim">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="receipt">
                {a.uploaded_by ?? 'founder'} · {fmtDate(a.created_at)} ·{' '}
                {a.repo_path ? 'filed in git' : a.r2_key ? 'stored in R2' : 'filing…'}
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 24, display: 'flex', justifyContent: 'center', zIndex: 40, pointerEvents: 'none' }}>
          <div className="toast">
            <span aria-hidden>✓</span>
            {toast}
          </div>
        </div>
      )}
    </Layout>
  );
}
